import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import api from "@api/api.js";
import t from "@/services/translationService.js";
import { formatDate, formatDateTime } from "@/services/formatDate.js";
import { viewLink } from "./rowActions.js";

const REFRESH_MS = 15_000;

function formatBytes(bytes) {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m ${total % 60}s`;
}

function formatMs(seconds) {
  return seconds == null ? "—" : `${(seconds * 1000).toFixed(1)} ms`;
}

/** Dwell time reads as "2m 14s", not "134000 ms". */
function formatDwell(ms) {
  if (ms == null) return "—";
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/**
 * Cockpit → Metrics. Reads GET /api/metrics/summary, the admin-gated JSON fold
 * of the same prom-client registry that backs the /metrics scrape target, and
 * polls it while the panel is on screen.
 *
 * props: { lang }
 */
export default class MetricsPanel extends CockpitPanel {
  constructor(props) {
    super(props);
    this.state = { data: null, error: "" };
    this.timer = null;
  }

  async onBeforeMount() {
    Object.assign(this.state, await this.fetchData());
  }

  async fetchData() {
    try {
      return { data: await api.get("/api/metrics/summary"), error: "" };
    } catch (err) {
      return { data: null, error: err.message || t.t("cockpit.metrics.loadError") };
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.metrics"));
  }

  lead() {
    return Component.escape(t.t("cockpit.metrics.lead"));
  }

  actions() {
    return `<button type="button" class="btn-secondary" data-action="refresh">${Component.escape(t.t("common.refresh"))}</button>`;
  }

  #stat(labelKey, value) {
    return `
      <div class="cockpit-stat">
        <span class="cockpit-stat__label">${Component.escape(t.t(labelKey))}</span>
        <span class="cockpit-stat__value">${Component.escape(value)}</span>
      </div>`;
  }

  /**
   * Gallery views, busiest first. Dwell is shown as "avg over N of M" rather
   * than a bare average: a visit only reports a duration if the browser ran the
   * leave handler, so the average always covers a subset and hiding that would
   * make a 1-of-40 average look as solid as a 40-of-40 one.
   */
  #galleryTable(galleries) {
    if (!galleries.length) return CockpitPanel.empty(t.t("cockpit.metrics.noVisits"));

    const rows = galleries
      .map(
        (gallery) => `
        <tr>
          <td>
            <span class="cockpit-row__name">${Component.escape(gallery.title || t.t("cockpit.metrics.untitled"))}</span>
          </td>
          <td class="cockpit-row__nowrap">
            ${gallery.visits}
            ${
              gallery.adminVisits
                ? `<span class="cockpit-row__muted">+${gallery.adminVisits} ${Component.escape(t.t("cockpit.metrics.ownViews"))}</span>`
                : ""
            }
          </td>
          <td class="cockpit-row__nowrap">
            ${Component.escape(formatDwell(gallery.avgDurationMs))}
            ${
              gallery.completedVisits < gallery.visits
                ? `<span class="cockpit-row__muted">${gallery.completedVisits}/${gallery.visits}</span>`
                : ""
            }
          </td>
          <td class="cockpit-row__nowrap">${Component.escape(
            formatDate(gallery.lastVisitAt, this.props.lang)
          )}</td>
          <td class="cockpit-table__actions">${viewLink(`#/gallery/${encodeURIComponent(gallery.galleryId)}`)}</td>
        </tr>`
      )
      .join("");

    return `
      <p class="cockpit-hint">${Component.escape(t.t("cockpit.metrics.visitsHint"))}</p>
      <div class="cockpit-table__wrap">
        <table class="cockpit-table">
          <thead>
            <tr>
              <th>${Component.escape(t.t("cockpit.metrics.colGallery"))}</th>
              <th>${Component.escape(t.t("cockpit.metrics.colVisits"))}</th>
              <th>${Component.escape(t.t("cockpit.metrics.colDwell"))}</th>
              <th>${Component.escape(t.t("cockpit.metrics.colLastVisit"))}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);
    const data = this.state.data;
    if (!data) return CockpitPanel.empty(t.t("common.loading"));

    const { process: proc, http } = data;

    const statusRows = Object.entries(http.byStatusClass || {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([statusClass, count]) =>
          `<span class="status-chip status-chip--${statusClass[0]}xx">${Component.escape(statusClass)} · ${count}</span>`
      )
      .join("");

    const routeRows = (http.topRoutes || [])
      .map(
        (route) => `
        <tr>
          <td><code class="cockpit-row__code">${Component.escape(route.route)}</code></td>
          <td class="cockpit-row__nowrap">${route.count}</td>
          <td class="cockpit-row__nowrap">${Component.escape(formatMs(route.avgDurationSeconds))}</td>
        </tr>`
      )
      .join("");

    return `
      <div class="cockpit-stats">
        ${this.#stat("cockpit.metrics.uptime", formatDuration(proc.uptimeSeconds))}
        ${this.#stat("cockpit.metrics.requests", String(http.totalRequests ?? 0))}
        ${this.#stat("cockpit.metrics.avgLatency", formatMs(http.avgDurationSeconds))}
        ${this.#stat("cockpit.metrics.memory", formatBytes(proc.residentMemoryBytes))}
        ${this.#stat("cockpit.metrics.heap", `${formatBytes(proc.heapUsedBytes)} / ${formatBytes(proc.heapTotalBytes)}`)}
        ${this.#stat("cockpit.metrics.eventLoop", formatMs(proc.eventLoopLagP99Seconds))}
        ${this.#stat("cockpit.metrics.cpu", proc.cpuSecondsTotal == null ? "—" : `${proc.cpuSecondsTotal.toFixed(1)} s`)}
        ${this.#stat("cockpit.metrics.node", proc.nodeVersion || "—")}
      </div>

      <h3 class="cockpit-subhead">${Component.escape(t.t("cockpit.metrics.galleryViews"))}</h3>
      ${this.#galleryTable(data.galleries || [])}

      <h3 class="cockpit-subhead">${Component.escape(t.t("cockpit.metrics.byStatus"))}</h3>
      <div class="cockpit-chips">${statusRows || CockpitPanel.empty(t.t("cockpit.metrics.noTraffic"))}</div>

      <h3 class="cockpit-subhead">${Component.escape(t.t("cockpit.metrics.topRoutes"))}</h3>
      ${
        routeRows
          ? `<div class="cockpit-table__wrap">
              <table class="cockpit-table">
                <thead>
                  <tr>
                    <th>${Component.escape(t.t("cockpit.metrics.colRoute"))}</th>
                    <th>${Component.escape(t.t("cockpit.metrics.colCount"))}</th>
                    <th>${Component.escape(t.t("cockpit.metrics.colAvg"))}</th>
                  </tr>
                </thead>
                <tbody>${routeRows}</tbody>
              </table>
            </div>`
          : CockpitPanel.empty(t.t("cockpit.metrics.noTraffic"))
      }

      <p class="cockpit-foot">${Component.escape(t.t("cockpit.metrics.collectedAt"))} ${Component.escape(formatDateTime(data.collectedAt, this.props.lang))}</p>`;
  }

  onMounted() {
    const refresh = this.$('[data-action="refresh"]');
    if (refresh) this.listen(refresh, "click", () => this.reload());

    // setState() re-runs onMounted, so clear the previous tick before arming
    // a new one — otherwise every refresh would leave another timer running.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.reload(), REFRESH_MS);
  }

  onBeforeUnmount() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  async reload() {
    this.setState(await this.fetchData());
  }
}
